import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { DatePicker, Select, Input, Button } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../../Service/Services.seguros";
import { ListarDoctorClinica } from "../../Service/Service.doctor";
import { ListadoPlanes } from "../../Service/Service.Plan";
import { GetPacientesAll } from "../../Service/Service.paciente";

const { RangePicker } = DatePicker;

// AUDITORIA-CLINICA-FASE-1 -- filtros SIEMPRE VISIBLES (misma regla UX ya
// usada en Reportes de Coberturas/Producción-Facturación), nunca ocultos
// tras un botón "más filtros".
// Catálogo REAL de `estado_citas` (verificado en BD). Antes esta lista estaba
// incompleta y con una etiqueta equivocada (decía 1 = "Programada" cuando 1 es
// "Sin Confirmar"), y al ofrecer solo 3 de los 8 estados era imposible filtrar
// para ver, por ejemplo, las Canceladas o Eliminadas.
const ESTADO_CITA = [
    { value: 1, label: "Sin Confirmar" },
    { value: 2, label: "Confirmado" },
    { value: 3, label: "En sala de espera" },
    { value: 4, label: "Reagendado" },
    { value: 5, label: "En Consulta" },
    { value: 6, label: "Finalizada" },
    { value: 7, label: "Cancelada" },
    { value: 8, label: "Eliminada" },
];

// Lo que Auditoría Clínica audita de verdad es la atención FINALIZADA, así que
// el listado abre filtrado por ese estado (antes abría con todos, y las citas
// Eliminadas con fecha futura encabezaban la lista). El filtro sigue
// permitiendo ver cualquier otro estado, o "Todos" limpiándolo.
export const ESTADO_CITA_FINALIZADA = 6;
const COBERTURA = [
    { value: "PARTICULAR", label: "Particular" },
    { value: "SEGURO", label: "Con seguro" },
    { value: "SIN_DETERMINAR", label: "Sin determinar" },
];
const CORREGIDA = [
    { value: "true", label: "Con correcciones" },
    { value: "false", label: "Sin correcciones" },
];

// AUDITORIA-CLINICA-WORKFLOW-44 §6: estado de REVISION. Es un filtro
// INDEPENDIENTE de "Correcciones" -- una atención auditada puede tener 0
// correcciones y una con correcciones puede seguir EN_PROCESO.
const ESTADO_AUDITORIA = [
    { value: "PENDIENTE", label: "Pendiente" },
    { value: "EN_PROCESO", label: "En proceso" },
    { value: "AUDITADA", label: "Auditada" },
];

export const AUDITORIA_FILTROS_EMPTY = {
    rangoFechas: null,
    paciente: undefined,
    documento: "",
    doctor: undefined,
    estado_cita: ESTADO_CITA_FINALIZADA,
    cobertura: undefined,
    aseguradora: undefined,
    plan: undefined,
    corregida: undefined,
    // WORKFLOW-44 §6: se deja SIN default ("Todos") a proposito -- el modulo
    // hasta ahora listaba todas las atenciones y forzar "Pendiente" por
    // defecto cambiaria el comportamiento ya establecido del listado. El
    // auditor lo elige explicitamente.
    estado_auditoria: undefined,
};

function AuditoriaClinicaFiltros({ draft, setDraft, onBuscar, onLimpiar }) {
    const [seguros, setSeguros] = useState([]);
    const [doctores, setDoctores] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [pacientes, setPacientes] = useState([]);

    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const seg = await ControllerListarSegurosClinica(idclinica);
            setSeguros((seg || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
            const doc = await ListarDoctorClinica(idclinica);
            const docData = doc?.data || doc || [];
            setDoctores((Array.isArray(docData) ? docData : []).map((d) => ({ value: d.iddoctor, label: d.nombre })));
            const pl = await ListadoPlanes({ estado: "Todos" });
            const plData = pl?.data || pl || [];
            setPlanes((Array.isArray(plData) ? plData : []).map((p) => ({ value: p.id_plan, label: p.nombre })));
        })();
    }, []);

    const buscarPacientes = async (search) => {
        const r = await GetPacientesAll(1, 15, search || "");
        if (r?.success) setPacientes(r.data.map((d) => ({ value: d.nombres_completos, label: `${d.cedula} - ${d.nombres_completos}` })));
    };

    return (
        <Row className="g-2 align-items-end mb-3 p-2 border rounded bg-light" data-testid="auditoria-clinica-filtros">
            <Col xs={12} md={4} lg={3}>
                <label className="form-label mb-1"><small>Rango de fechas</small></label>
                <RangePicker className="w-100" value={draft.rangoFechas} onChange={(d) => setDraft((f) => ({ ...f, rangoFechas: d }))} getPopupContainer={() => document.body} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Paciente</small></label>
                <Select className="w-100" allowClear showSearch filterOption={false} placeholder="Buscar" options={pacientes} value={draft.paciente} onSearch={buscarPacientes} onChange={(v) => setDraft((f) => ({ ...f, paciente: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Documento</small></label>
                <Input allowClear placeholder="Cédula" value={draft.documento} onChange={(e) => setDraft((f) => ({ ...f, documento: e.target.value }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Doctor</small></label>
                <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todos" options={doctores} value={draft.doctor} onChange={(v) => setDraft((f) => ({ ...f, doctor: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1" htmlFor="filtro-estado-cita"><small>Estado de la cita</small></label>
                <Select id="filtro-estado-cita" className="w-100" allowClear options={ESTADO_CITA} value={draft.estado_cita} onChange={(v) => setDraft((f) => ({ ...f, estado_cita: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Cobertura</small></label>
                <Select className="w-100" allowClear options={COBERTURA} value={draft.cobertura} onChange={(v) => setDraft((f) => ({ ...f, cobertura: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Aseguradora</small></label>
                <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={seguros} value={draft.aseguradora} onChange={(v) => setDraft((f) => ({ ...f, aseguradora: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Plan</small></label>
                <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todos" options={planes} value={draft.plan} onChange={(v) => setDraft((f) => ({ ...f, plan: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Correcciones</small></label>
                <Select className="w-100" allowClear options={CORREGIDA} value={draft.corregida} onChange={(v) => setDraft((f) => ({ ...f, corregida: v }))} />
            </Col>
            <Col xs={6} md={4} lg={2}>
                <label className="form-label mb-1"><small>Estado auditoría</small></label>
                <Select
                    className="w-100"
                    allowClear
                    placeholder="Todos"
                    options={ESTADO_AUDITORIA}
                    value={draft.estado_auditoria}
                    onChange={(v) => setDraft((f) => ({ ...f, estado_auditoria: v }))}
                />
            </Col>
            <Col xs={12} className="d-flex gap-2 mt-2">
                <Button type="primary" icon={<SearchOutlined />} onClick={onBuscar}>Buscar</Button>
                <Button icon={<ClearOutlined />} onClick={onLimpiar}>Limpiar</Button>
            </Col>
        </Row>
    );
}

export default AuditoriaClinicaFiltros;
